import { useCallback, useEffect, useState } from 'react'
import { api, type BalanceInfo } from '../lib/api'

const CACHE_KEY = (addr: string) => `imago:balance:${addr}`

function loadCached(tonAddress: string | undefined): BalanceInfo | null {
  if (!tonAddress) return null
  try {
    const raw = localStorage.getItem(CACHE_KEY(tonAddress))
    return raw ? (JSON.parse(raw) as BalanceInfo) : null
  } catch {
    return null
  }
}

function saveCache(tonAddress: string, info: BalanceInfo) {
  try {
    localStorage.setItem(CACHE_KEY(tonAddress), JSON.stringify(info))
  } catch { /* quota */ }
}

export function useBalance(_apiKey: string | null, tonAddress: string | undefined) {
  // Initialize from cache so balance shows instantly on app open (no $0 flash)
  const [balance, setBalance] = useState<BalanceInfo | null>(() => loadCached(tonAddress))
  const [isLoading, setIsLoading] = useState(false)

  // When tonAddress changes (wallet connects/disconnects), sync balance state
  useEffect(() => {
    if (!tonAddress) {
      setBalance(null)
      return
    }
    const cached = loadCached(tonAddress)
    if (cached) setBalance(cached)
  }, [tonAddress])

  // NOTE: getBalance is a public endpoint — no apiKey required.
  // We only need tonAddress. apiKey kept as param for API consistency but
  // balance fetches as soon as wallet connects, without waiting for apiKey.
  const refresh = useCallback(async () => {
    if (!tonAddress) return
    setIsLoading(true)
    try {
      const info = await api.getBalance(tonAddress)
      setBalance(info)
      saveCache(tonAddress, info)
    } catch (err) {
      // Log error for debugging but keep showing cached/last balance
      console.error('[useBalance] fetch failed:', err)
    } finally {
      setIsLoading(false)
    }
  }, [tonAddress])

  useEffect(() => {
    refresh()
    // Poll every 10s to catch deposits quickly
    const interval = setInterval(refresh, 10_000)
    return () => clearInterval(interval)
  }, [refresh])

  return { balance, isLoading, refresh }
}
